import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Modal, Box, TextField, Typography, Alert, Grid, TablePagination, CircularProgress,
    Select, MenuItem, FormControl, InputLabel, InputAdornment, Collapse, IconButton, Chip, Tooltip
} from '@mui/material';
import { fontWeight, styled } from '@mui/system';
import { Search, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { makeApiRequest } from '../../../utils/api';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
    textAlign: 'center',
    '&:first-child': {
        width: '5%',
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: '#f5f5f5',
    },
}));

const Course = () => {
    const navigate = useNavigate();
    const control = Number(localStorage.getItem('control'));
    const canEditCourse = control === 1 || control === 4;
    const [courseData, setCourseData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(null);
    const [editedCourseName, setEditedCourseName] = useState('');
    const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
    const [statusToUpdate, setStatusToUpdate] = useState('');
    const [status, setStatus] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentBatchName, setCurrentBatchName] = useState('');
    const [originalCourseName, setOriginalCourseName] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [currentItems, setCurrentItems] = useState([]);
    const [batches, setBatches] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState('');
    const [newCourseName, setNewCourseName] = useState('');
    const [allCourseData, setAllCourseData] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [allData, setAllData] = useState([]);
    const [expandedRow, setExpandedRow] = useState(null);
    const [moduleData, setModuleData] = useState([]);
    const [courseList, setCourseList] = useState([]);
    const [assessmentsData, setAssessmentsData] = useState({});
    const [chapterModalOpen, setChapterModalOpen] = useState(false);
    const [chapterModalModule, setChapterModalModule] = useState(null);
    const [chapterModalCourseId, setChapterModalCourseId] = useState(null);
    const [moduleChapters, setModuleChapters] = useState([]);
    const [chapterListLoading, setChapterListLoading] = useState(false);
    const [newChapterName, setNewChapterName] = useState('');
    const [addingChapter, setAddingChapter] = useState(false);


    useEffect(() => {
        const fetchCourseData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await makeApiRequest('post', '/api/course/list', {
                    token: localStorage.getItem('authToken'),
                });
                if (!response || response.status !== 'success' || !response.data) {
                    throw new Error(response?.message || 'Invalid response from API');
                }
                setCourseList(response.data);
            }
            catch (err) {
                console.error("Error fetching batches:", err);
                setError(err.message || 'Error fetching data');
                setCourseList([]);
            } finally {
                setLoading(false);
            }
        }
        fetchCourseData();

    }, []);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };


    const fetchModule = async (course_id) => {
        try {
            const response = await makeApiRequest('post', '/api/module/list/course', {
                token: localStorage.getItem('authToken'),
                Course_id: course_id
            });
            if (response.status === "success") {
                setModuleData(response.data);

            }

        } catch (err) {
            console.error("Error fetching batches:", err);
            setError(err.message || 'Error fetching data');
            setBatches([]);
        }
    }

    const fetchModuleChapters = async (moduleId) => {
        setChapterListLoading(true);
        try {
            const response = await makeApiRequest('post', '/api/chapter/listbyModule', {
                token: localStorage.getItem('authToken'),
                id: moduleId,
            });
            if (response.status === 'success') {
                setModuleChapters(response.data || []);
            } else {
                setModuleChapters([]);
            }
        } catch (err) {
            console.error('Error fetching chapters for module:', err);
            setModuleChapters([]);
        } finally {
            setChapterListLoading(false);
        }
    };

    const openChapterModal = (module, courseId) => {
        setChapterModalModule(module);
        setChapterModalCourseId(courseId);
        setNewChapterName('');
        setChapterModalOpen(true);
        fetchModuleChapters(module.module_id);
    };

    const closeChapterModal = () => {
        setChapterModalOpen(false);
        setChapterModalModule(null);
        setChapterModalCourseId(null);
        setModuleChapters([]);
        setNewChapterName('');
    };

    const handleAddChapterToModule = async () => {
        if (!newChapterName.trim()) {
            toast.warn('Please enter a chapter name');
            return;
        }

        setAddingChapter(true);
        try {
            const response = await makeApiRequest('post', '/api/chapter/add', {
                token: localStorage.getItem('authToken'),
                module_id: chapterModalModule.module_id,
                chapter_name: newChapterName,
                chapter_status: 1,
                userid: localStorage.getItem('userid') || localStorage.getItem('user_id'),
            });

            if (response.status === 'success') {
                toast.success('Chapter added successfully');
                setNewChapterName('');
                await fetchModuleChapters(chapterModalModule.module_id);
                if (chapterModalCourseId) {
                    fetchModule(chapterModalCourseId);
                }
            } else {
                throw new Error('Failed to add chapter');
            }
        } catch (err) {
            console.error('Error adding chapter:', err);
            toast.error(err.response?.data?.message || 'Failed to add chapter');
        } finally {
            setAddingChapter(false);
        }
    };

    const fetchAssessments = async (course_id) => {
        try {
            const response = await makeApiRequest('post', '/api/batch/assessments', {
                token: localStorage.getItem('authToken'),
                batch_id: 0,
                course_id: course_id
            });
            if (response.status === 'success') {
                setAssessmentsData(prev => ({
                    ...prev,
                    [course_id]: response.data
                }));
            }
        } catch (err) {
            console.error('Error fetching assessments:', err);
        }
    };
    const filteredCourses = courseList.filter(course =>
        course.course_name.toLowerCase().includes(searchTerm)
    );
    if (loading) return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
        >
            <CircularProgress
                size={40}
                thickness={4}
                sx={{
                    color: '#481E82',
                }}
            />
        </Box>
    );



    return (
        <Box sx={{ height: "auto", backgroundColor: "#ECEDFF" }}>
            <ToastContainer position="top-center" autoClose={2000} theme="light" />
            <Grid container sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Grid item xs={11}>
                    <Box container sx={{ height: "80px", display: "flex", justifyContent: "start", alignItems: "center", backgroundColor: "#ECEDFF" }}>
                        <Typography variant="h4" sx={{
                            color: "#481E82", fontWeight: "500", fontFamily: 'Roboto'
                        }}> All Courses</Typography>
                    </Box>
                    <Grid item xs={12} sx={{ backgroundColor: 'white', padding: "20px", borderRadius: "5px 5px 0 0" }}>
                        {/* <Box sx={{ width: "50%", display: "flex", alignItems: "center" }}> */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    placeholder="Search Course"
                                    value={searchTerm}
                                    size="small"
                                    onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                                    sx={{
                                        width: { xs: "100%", md: "300px" }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>


                        {/* </Box> */}
                    </Grid>
                    <Box>
                        <TableContainer component={Paper} sx={{ borderRadius: "0px" }}>
                            <Table sx={{ minWidth: 650, borderRadius: "0px" }} aria-label="course table">
                                <TableHead sx={{ backgroundColor: '#ECEDFF', height: "60px", marginTop: "0px", margin: "0px", borderRadius: "0px" }}>
                                    <TableRow sx={{ borderRadius: "0px" }}>
                                        <StyledTableCell sx={{ color: 'black', fontSize: "14px", fontFamily: "Roboto" }}><strong>S/No.</strong></StyledTableCell>
                                        <StyledTableCell sx={{ color: 'black', fontSize: "14px", textAlign: "start", fontFamily: "Roboto" }}><strong>Course Name</strong></StyledTableCell>
                                        {/* <StyledTableCell sx={{ color: 'black', fontSize: "14px", textAlign: "start", fontFamily: "Roboto" }}><strong>Batch</strong></StyledTableCell> */}
                                        <StyledTableCell sx={{ color: 'black', fontSize: "14px", fontFamily: "Roboto" }}><strong>Modules</strong></StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredCourses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                                        <React.Fragment key={index}>
                                            <TableRow sx={{
                                                '& > *': { borderBottom: 'unset' },
                                                backgroundColor: expandedRow === index ? '#f9f9ff' : 'white',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                }
                                            }}>
                                                <StyledTableCell component="th" scope="row">
                                                    <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>{index + 1 + page * rowsPerPage}</Typography>

                                                </StyledTableCell>
                                                <StyledTableCell component="th" scope="row">
                                                    <Typography sx={{ fontWeight: 500, fontSize: "14px", color: "black", textAlign: "start" }}>{item.course_name}</Typography>
                                                </StyledTableCell>
                                                {/* <StyledTableCell component="th" scope="row">
                                                    <Typography sx={{ fontWeight: 500, fontSize: "14px", color: "black", textAlign: "start" }}>{item.batch_name}</Typography>
                                                </StyledTableCell> */}
                                                <StyledTableCell>
                                                    <IconButton
                                                        aria-label="expand row"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            fetchModule(item.id); // Ensure course ID is correct
                                                            fetchAssessments(item.id);
                                                            setExpandedRow(expandedRow === index ? null : index); // Toggle row expansion
                                                        }}
                                                    >
                                                        {expandedRow === index ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                    </IconButton>
                                                </StyledTableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                                    <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                                                        <Box sx={{ padding: 2, backgroundColor: '#eeeeff' }}>
                                                            <Grid container spacing={4}>
                                                                <Grid item xs={12} md={6}>
                                                                    <Typography variant="h6" gutterBottom component="div" sx={{ color: '#481E82', fontWeight: 'bold' }}>
                                                                        Modules of {item.course_name}
                                                                    </Typography>
                                                                    {moduleData.length > 0 ? (
                                                                        <Table size="small" aria-label="modules">
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell sx={{ fontWeight: 'bold' }}>S/NO</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 'bold' }}>Module Name</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {moduleData.map((module, index) => {
                                                                                    const hasNoChapters = Number(module.chapters_count) === 0;
                                                                                    return (
                                                                                        <TableRow
                                                                                            key={index}
                                                                                            sx={hasNoChapters ? { backgroundColor: '#fff3e0' } : undefined}
                                                                                        >
                                                                                            <TableCell>{index + 1}</TableCell>
                                                                                            <TableCell>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                    {module.name}
                                                                                                    {hasNoChapters && (
                                                                                                        <>
                                                                                                            <Tooltip title="This module has no chapters yet">
                                                                                                                <Chip
                                                                                                                    label="No Chapters"
                                                                                                                    size="small"
                                                                                                                    sx={{ backgroundColor: '#FFE0B2', color: '#E65100', fontWeight: 600 }}
                                                                                                                />
                                                                                                            </Tooltip>
                                                                                                            <Tooltip title="Add chapters to this module">
                                                                                                                <IconButton
                                                                                                                    size="small"
                                                                                                                    onClick={() => openChapterModal(module, item.id)}
                                                                                                                >
                                                                                                                    <EditIcon fontSize="small" sx={{ color: '#481E82' }} />
                                                                                                                </IconButton>
                                                                                                            </Tooltip>
                                                                                                        </>
                                                                                                    )}
                                                                                                </Box>
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    );
                                                                                })}
                                                                            </TableBody>
                                                                        </Table>
                                                                    ) : (
                                                                        <Typography>No modules found for this course.</Typography>
                                                                    )}
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                         <Typography variant="h6" sx={{ color: '#481E82', fontWeight: 'bold' }}>
                                                                            Linked Assessments
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                                            {canEditCourse && (
                                                                                <Button
                                                                                    variant="outlined"
                                                                                    size="small"
                                                                                    onClick={() => navigate(`/edit-course?courseId=${item.id}`)}
                                                                                    sx={{ color: '#481E82', borderColor: '#481E82', '&:hover': { borderColor: '#3b186b', backgroundColor: 'rgba(72, 30, 130, 0.04)' } }}
                                                                                >
                                                                                    Edit Course
                                                                                </Button>
                                                                            )}
                                                                            <Button
                                                                                variant="contained"
                                                                                size="small"
                                                                                onClick={() => navigate(`/linked-assessments?courseId=${item.id}`)}
                                                                                sx={{ backgroundColor: '#481E82', '&:hover': { backgroundColor: '#3b186b' } }}
                                                                            >
                                                                                View Rubrics
                                                                            </Button>
                                                                        </Box>
                                                                    </Box>
                                                                    {assessmentsData[item.id] && assessmentsData[item.id].length > 0 ? (
                                                                        <Table size="small" aria-label="rubrics">
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell sx={{ fontWeight: 'bold' }}>S/NO</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 'bold' }}>Assessment Name</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 'bold' }}>Display Name</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {assessmentsData[item.id].map((ass, index) => (
                                                                                    <TableRow key={index}>
                                                                                        <TableCell>{index + 1}</TableCell>
                                                                                        <TableCell>{ass.generic_name}</TableCell>
                                                                                        <TableCell>{ass.specific_name}</TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    ) : (
                                                                        <Typography>No linked assessments found for this course.</Typography>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[10, 20, 50]}
                            component="div"
                            count={courseList.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />

                    </Box>
                </Grid>
            </Grid>
            <Modal
                open={chapterModalOpen}
                onClose={closeChapterModal}
                aria-labelledby="modal-add-chapter-title"
            >
                <Box sx={{ position: 'absolute', top: 80, left: '50%', transform: 'translateX(-50%)', width: 450, maxWidth: '90%', backgroundColor: 'white', borderRadius: 2, padding: 4, boxShadow: 24 }}>
                    <Typography variant="h6" id="modal-add-chapter-title" sx={{ color: '#481E82', fontWeight: 'bold', mb: 1 }}>
                        Chapters for {chapterModalModule?.name}
                    </Typography>

                    <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                        {chapterListLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : moduleChapters.length > 0 ? (
                            moduleChapters.map((chapter, idx) => (
                                <Typography key={chapter.chapter_id || idx} sx={{ fontSize: '14px', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                                    {idx + 1}. {chapter.name}
                                </Typography>
                            ))
                        ) : (
                            <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                                No chapters added to this module yet.
                            </Typography>
                        )}
                    </Box>

                    <TextField
                        fullWidth
                        label="New Chapter Name"
                        variant="outlined"
                        size="small"
                        value={newChapterName}
                        onChange={(e) => setNewChapterName(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button variant="outlined" color="secondary" onClick={closeChapterModal}>
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleAddChapterToModule}
                            disabled={addingChapter}
                            sx={{ backgroundColor: '#481E82', '&:hover': { backgroundColor: '#3b186b' } }}
                        >
                            {addingChapter ? 'Adding...' : 'Add Chapter'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default Course;